import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { User, Camera, LogOut, Shield, Wallet, ChevronRight, Check, X, ShieldCheck, AlertCircle } from 'lucide-react';
import { useDevice } from '../hooks/useDevice';
import { toast } from 'react-hot-toast';

const Profile = () => {
    const { user, logout } = useAuth();
    const { isMobile } = useDevice();
    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const uploadMutation = useMutation({
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            return (await client.post('/users/profile/photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })).data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['auth_me']);
            toast.success('Фото обновлено');
        },
        onSettled: () => setUploading(false)
    });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploading(true);
            uploadMutation.mutate(file);
        }
    };

    const handleLogout = () => {
        if (window.confirm("Вы уверены, что хотите выйти?")) logout();
    };

    const avatarUrl = user?.photo_url ? (user.photo_url.startsWith('/') ? (user.photo_url.startsWith('/api') ? user.photo_url : `/api${user.photo_url}`) : user.photo_url) : null;

    return (
        <div className={`max-w-4xl mx-auto space-y-12 animate-in fade-in duration-700 ${isMobile ? 'pb-24 pt-4' : 'pb-32 pt-12'}`}>
            {/* Profile Header */}
            <div className={`flex flex-col items-center ${isMobile ? '' : 'bg-neutral-900 border border-neutral-800 p-12 rounded-[5rem] shadow-2xl relative overflow-hidden'}`}>
                {!isMobile && <div className="absolute top-0 right-0 p-20 opacity-5 -mr-20 -mt-20"><User size={200} /></div>}
                <div className="relative group italic">
                    <div className={`${isMobile ? 'w-32 h-32 rounded-[2.5rem]' : 'w-48 h-48 rounded-[4rem]'} bg-neutral-950 border border-neutral-800 overflow-hidden flex items-center justify-center relative shadow-2xl group-hover:border-blue-500/50 transition-all duration-500`}>
                        {avatarUrl ? (
                            <img src={avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                            <User size={isMobile ? 64 : 96} className="text-neutral-700" />
                        )}
                        {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div></div>}
                    </div>
                    <button onClick={() => fileInputRef.current?.click()} className={`absolute ${isMobile ? '-bottom-2 -right-2 p-3 rounded-2xl' : '-bottom-4 -right-4 p-5 rounded-[2rem]'} bg-blue-600 text-white shadow-2xl shadow-blue-600/30 hover:scale-110 active:scale-95 transition-all border-4 border-neutral-950`}><Camera size={isMobile ? 20 : 28} /></button>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                </div>
                <h1 className={`${isMobile ? 'text-2xl mt-6' : 'text-5xl mt-10'} font-black text-white uppercase italic tracking-tighter`}>{user?.fio || user?.name || 'Пользователь'}</h1>
                <p className="text-neutral-500 font-bold text-xs uppercase tracking-[0.4em] mt-2 italic">{user?.role === 'superadmin' ? 'Strategic Architect' : user?.role || 'User'}</p>
            </div>

            {/* Stats / Info */}
            <div className={`grid grid-cols-2 ${isMobile ? 'gap-4' : 'gap-10'}`}>
                <div className="bg-neutral-900 border border-neutral-800 p-8 rounded-[3rem] shadow-xl italic">
                    <p className="text-[10px] font-black text-neutral-600 uppercase tracking-widest mb-1 italic">Telegram ID</p>
                    <p className="text-2xl font-black text-white italic tracking-tighter">{user?.telegram_id || 'Not Linked'}</p>
                </div>
                <div className="bg-neutral-900 border border-neutral-800 p-8 rounded-[3rem] shadow-xl italic">
                    <p className="text-[10px] font-black text-neutral-600 uppercase tracking-widest mb-1 italic">Account Tier</p>
                    <p className="text-2xl font-black text-blue-500 italic tracking-tighter uppercase">{user?.role || 'Guest'}</p>
                </div>
            </div>

            {/* Menu Sections */}
            <div className="space-y-4 italic">
                <h3 className="px-6 text-[10px] font-black text-neutral-600 uppercase tracking-[0.4em] italic">System Controls</h3>
                
                <button className="w-full bg-neutral-900 border border-neutral-800 p-8 rounded-[3rem] flex items-center justify-between hover:border-blue-500/30 transition-all group">
                    <div className="flex items-center gap-6">
                        <div className={`p-4 rounded-2xl flex items-center justify-center transition-colors ${user?.is_2fa_enabled ? 'bg-emerald-600 text-white' : 'bg-neutral-950 text-neutral-700'}`}><ShieldCheck size={28} /></div>
                        <div className="text-left">
                            <span className="text-lg font-black text-white uppercase italic block">Двухфакторная защита (2FA)</span>
                            <span className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest italic">{user?.is_2fa_enabled ? 'Active Secure Protocol' : 'Vulnerable Connection'}</span>
                        </div>
                    </div>
                    <div className={`w-14 h-8 rounded-full p-1 transition-all ${user?.is_2fa_enabled ? 'bg-emerald-600' : 'bg-neutral-800'}`}><div className={`w-6 h-6 bg-white rounded-xl transition-all ${user?.is_2fa_enabled ? 'translate-x-6' : 'translate-x-0'}`}></div></div>
                </button>

                <button onClick={handleLogout} className="w-full bg-rose-600/5 border border-rose-900/10 p-8 rounded-[3rem] flex items-center justify-center gap-4 hover:bg-rose-600/10 transition-all group mt-8 shadow-2xl">
                    <LogOut size={24} className="text-rose-500" />
                    <span className="text-lg font-black text-rose-500 uppercase italic">Terminate Session</span>
                </button>
            </div>

            <div className="text-center italic opacity-30 pt-10">
                <p className="text-[10px] text-neutral-500 font-black uppercase tracking-[0.6em] italic">BroBrand Digital Infrastructure v4.2</p>
            </div>
        </div>
    );
};

export default Profile;
